/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@rbx/ui';
import useTryItOut from '../hooks/useTryItOut';
import TryItOutExecutionWarningDialog from './tryItOut/TryItOutExecutionWarningDialog';

// https://github.com/swagger-api/swagger-ui/blob/04b9ad7b37f02bce0e1cb8c521e40b68585ccf9f/src/core/components/execute.jsx
const DetailsSwaggerUIExecute = (props: any) => {
  const {
    specSelectors,
    specActions,
    operation,
    path,
    method,
    oas3Selectors,
    oas3Actions,
    onExecute,
    disabled,
  } = props;

  const { showExecutionWarning } = useTryItOut();
  const [executionWarningOpen, setExecutionWarningOpen] = useState(false);

  // Validate parameters for the API call
  const validateParams = useCallback(() => {
    specActions.validateParams([path, method]);
    return specSelectors.validateBeforeExecute([path, method]);
  }, [specActions, specSelectors, path, method]);

  // Validate request body for the API call
  const validateRequestBody = useCallback(() => {
    const validationErrors = {
      missingBodyValue: false,
      missingRequiredKeys: [] as string[],
    };

    // Reset errors, then re-validate
    oas3Actions.clearRequestBodyValidateError({ path, method });
    const oas3RequiredRequestBodyContentType = specSelectors.getOAS3RequiredRequestBodyContentType([
      path,
      method,
    ]);
    const oas3RequestBodyValue = oas3Selectors.requestBodyValue(path, method);
    const oas3ValidateBeforeExecuteSuccess = oas3Selectors.validateBeforeExecute([path, method]);
    const oas3RequestContentType = oas3Selectors.requestContentType(path, method);

    if (!oas3ValidateBeforeExecuteSuccess) {
      validationErrors.missingBodyValue = true;
      oas3Actions.setRequestBodyValidateError({ path, method, validationErrors });
      return false;
    }

    if (!oas3RequiredRequestBodyContentType) {
      return true;
    }

    const missingRequiredKeys = oas3Selectors.validateShallowRequired({
      oas3RequiredRequestBodyContentType,
      oas3RequestContentType,
      oas3RequestBodyValue,
    });

    if (!missingRequiredKeys || missingRequiredKeys.length < 1) {
      return true;
    }

    missingRequiredKeys.forEach((missingKey: string) => {
      validationErrors.missingRequiredKeys.push(missingKey);
    });
    oas3Actions.setRequestBodyValidateError({ path, method, validationErrors });
    return false;
  }, [specSelectors, oas3Actions, oas3Selectors, path, method]);

  const handleSuccessfulValidation = useCallback(() => {
    if (onExecute) {
      onExecute(); // Show loading spinner
    }
    specActions.execute({ operation, path, method });
  }, [onExecute, specActions, operation, path, method]);

  const handleValidationFailure = useCallback(() => {
    // Deferred by 40ms to give element class change time to settle
    specActions.clearValidateParams([path, method]);
    setTimeout(() => {
      specActions.validateParams([path, method]);
    }, 40);
  }, [specActions, path, method]);

  const executeApiCall = useCallback(() => {
    const paramsResult = validateParams();
    const requestBodyResult = validateRequestBody();
    const isPass = paramsResult && requestBodyResult;

    if (isPass) {
      handleSuccessfulValidation();
    } else {
      handleValidationFailure();
    }
  }, [validateParams, validateRequestBody, handleSuccessfulValidation, handleValidationFailure]);

  const onClick = useCallback(
    (event: React.MouseEvent) => {
      if (showExecutionWarning) {
        event.preventDefault();
        event.stopPropagation();
        // Blur the button to remove focus before opening dialog (prevents aria-hidden violation)
        (event.currentTarget as HTMLElement).blur();
        setExecutionWarningOpen(true);
        return;
      }

      executeApiCall();
    },
    [showExecutionWarning, executeApiCall],
  );

  return (
    <Fragment>
      <Button
        variant='contained'
        color='primary'
        className='btn execute opblock-control__btn'
        onClick={onClick}
        disabled={disabled}>
        Execute
      </Button>
      <TryItOutExecutionWarningDialog
        executionWarningOpen={executionWarningOpen}
        setExecutionWarningOpen={setExecutionWarningOpen}
        onContinue={executeApiCall}
      />
    </Fragment>
  );
};

export default DetailsSwaggerUIExecute;
